'use client';

import { CheckboxInput } from '@app/app/_components/Input/CheckboxInput';
import { ChoiceButtonInput } from '@app/app/_components/Input/ChoiceButtonInput';
import { Paragraph } from '@app/app/_components/Paragraph/Paragraph';
import { useAppParameters } from '@app/app/_context/AppParametersContext/useAppParameters';
import { MasterCardLogo } from '@app/app/_icons/MasterCardLogo';
import { MoneyIcon } from '@app/app/_icons/MoneyIcon';
import { PaymentMethodType } from '@app/domain/model/PaymentMethodType';
import { FormState } from '@app/lib/form/contracts/FormState';
import { PurchaseTravelInsuranceResult } from '@app/service/eshop/travel/actions/purchaseTravelInsurance';
import { useTranslations } from 'next-intl';
import Link from 'next/link';
import { ChangeEvent, useState } from 'react';

interface Props {
    formState: FormState<PurchaseTravelInsuranceResult>;
    onChange?: (paymentMethodType: Nullable<string>) => void;
}

export function PaymentMethodTypeStep({ formState, onChange }: Props) {
    const t = useTranslations();
    const parameters = useAppParameters();

    const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<Nullable<string>>(null);

    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setSelectedPaymentMethod(value);

        onChange?.(value);
    };

    return (
        <div className="flex flex-col gap-10">
            <ChoiceButtonInput
                name="paymentMethodType"
                inputContainerClassName="self-center"
                onChange={handleChange}
                options={[
                    {
                        value: PaymentMethodType.Banks,
                        label: (
                            <div className="flex items-center gap-4">
                                <MoneyIcon className="w-10 text-primary-500" />
                                <div className="text-lg font-medium text-neutral-900">
                                    {t('eshop.general.paymentBanks')}
                                </div>
                            </div>
                        ),
                    },
                    {
                        value: PaymentMethodType.Card,
                        label: (
                            <div className="flex items-center gap-4">
                                <MasterCardLogo className="w-10" />
                                <div className="text-lg font-medium text-neutral-900">
                                    {t('eshop.general.paymentVisaMastercard')}
                                </div>
                            </div>
                        ),
                    },
                ]}
            />

            {selectedPaymentMethod === PaymentMethodType.Card && (
                <div className="flex flex-col gap-6">
                    <Paragraph>{t('eshop.travel.paymentMethodDescription')}</Paragraph>

                    <CheckboxInput
                        name="acceptCardAndPaymentProviderRules"
                        label={t.rich('eshop.travel.agreeToPaymentProviderRules', {
                            link: chunks => (
                                <Link href={parameters.eshopInfoUrls.blueEmiLink} className="underline" target="_blank">
                                    {chunks}
                                </Link>
                            ),
                        })}
                        error={formState.violations?.acceptCardAndPaymentProviderRules}
                    />
                </div>
            )}
        </div>
    );
}
